import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import styles from "./final-form-create-study.module.scss";
import checkGreenIcon from "../../../../images/approve.svg";
import checkGrayIcon from "../../../../images/approve-gray.svg";
import connectionIcon from "../../../../images/connection.svg";
import Tooltip from "../../../ui-kit/tooltip/tooltip";
import { useAppSelector } from "../../../../hooks/useAppSelector";

export type StatusType = "available" | "empty" | "lost";

const statusIconMap: Record<StatusType, string> = {
  available: checkGreenIcon,
  empty: checkGrayIcon,
  lost: connectionIcon,
};

const StatusIcon: FC<{ status: StatusType }> = ({ status }) => {
  const { t } = useTranslation();
  return (
    <span className={styles.status}>
      <img
        src={statusIconMap[status]}
        alt={status}
        className={styles.iconImage}
      />
      {status === "available" && t("main-form-create-study.statusAvailable")}
      {status === "empty" && t("main-form-create-study.statusEmpty")}
      {status === "lost" && t("main-form-create-study.statusLostConnection")}
    </span>
  );
};

const FinalFormCreateStudy = () => {
  const { t } = useTranslation();
  const {
    bufferInfrastructureResults,
    externalServicesAvailability,
    mainForm,
  } = useAppSelector((state) => state.studyCreation);

  const hasAnyNetwork = Object.values(bufferInfrastructureResults).some(
    ({ available }) => available,
  );

  const externalServices = [
    { name: "GDB", status: hasAnyNetwork ? "available" : "empty" },
    {
      name: "NCSI",
      status: externalServicesAvailability.ncsi ? "available" : "lost",
    },
    {
      name: "OPIS",
      status: externalServicesAvailability.opis ? "available" : "lost",
    },
  ] as const;

  return (
    <div>
      <div className={styles.grid}>
        <span className={styles.label}>{t("main-form-create-study.cityNameSummaryLabel")}</span>
        <p className={styles.value}>{mainForm.name}</p>

        <div className={styles.label}>{t("main-form-create-study.externalServicesLabel")}</div>
        <div className={styles.value}>
          <div className={styles.services}>
            {externalServices.map((service) => {
              const disabled = service.status == "lost";
              return (
                <Tooltip
                  key={service.name}
                  classes={{ text: styles.tooltipText }}
                  disabled={!disabled}
                  text={t("main-form-create-study.externalServicesTooltip")}
                >
                  <div
                    className={`${styles.serviceButton} ${disabled ? styles.disabled : ""}`}
                  >
                    <img
                      src={statusIconMap[service.status]}
                      alt={service.status}
                      className={styles.iconImage}
                    />
                    {service.name}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>
      <div className={styles.analyticalReports}>
        <span className={styles.label}>{t("main-form-create-study.analyticalReportsLabel")}</span>
        <p>{t('main-form-create-study.analyticalReportsDescription')}</p>
        <div className={styles.reports}>
          {Object.entries(bufferInfrastructureResults).map(
            ([key, { available }]) => (
              <React.Fragment key={key}>
                <div className={styles.label}>{t(`main-form-create-study.infraLabels.${key}`)}</div>
                <StatusIcon status={available ? "available" : "empty"} />
              </React.Fragment>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default FinalFormCreateStudy;
